import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Building2, Phone, Mail, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const inputClass = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#0073ea]/50 focus:ring-[#0073ea]";

const defaultForm = { name: '', email: '', phone: '', company: '', deals_sold: '', notes: '' };

export default function WholesalersPage() {
  const [wholesalers, setWholesalers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const params = search ? { search } : {};
      const res = await API.get('/wholesalers', { params });
      setWholesalers(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form };
      if (payload.deals_sold) payload.deals_sold = parseInt(payload.deals_sold) || 0;
      else payload.deals_sold = 0;
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });

      if (editId) {
        await API.put(`/wholesalers/${editId}`, payload);
        toast.success('Wholesaler updated');
      } else {
        await API.post('/wholesalers', payload);
        toast.success('Wholesaler added');
      }
      setDialogOpen(false);
      setForm(defaultForm);
      setEditId(null);
      fetch();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed';
      toast.error(msg);
    } finally { setCreating(false); }
  };

  const handleEdit = (w) => {
    setForm({
      name: w.name || '',
      email: w.email || '',
      phone: w.phone || '',
      company: w.company || '',
      deals_sold: w.deals_sold?.toString() || '',
      notes: w.notes || '',
    });
    setEditId(w.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/wholesalers/${id}`);
      toast.success('Wholesaler deleted');
      fetch();
    } catch { toast.error('Failed to delete'); }
  };

  const openCreate = () => {
    setForm(defaultForm);
    setEditId(null);
    setDialogOpen(true);
  };

  return (
    <div data-testid="wholesalers-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Wholesalers</h1>
          <p className="text-gray-500 mt-1">{wholesalers.length} wholesaler{wholesalers.length !== 1 ? 's' : ''}</p>
        </div>
        <Button data-testid="add-wholesaler-btn" onClick={openCreate} className="bg-[#0073ea] hover:bg-[#0060c2] text-white">
          <Plus className="h-4 w-4 mr-2" /> Add Wholesaler
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          data-testid="wholesaler-search"
          placeholder="Search by name, company, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={`pl-9 ${inputClass}`}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 bg-gray-200 rounded-xl" />)}
        </div>
      ) : wholesalers.length === 0 ? (
        <Card className="bg-white border-gray-200 border-dashed shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Building2 className="h-12 w-12 text-gray-300 mb-4" strokeWidth={1.5} />
            <p className="text-gray-500 text-lg font-medium">No wholesalers yet</p>
            <p className="text-gray-400 text-sm mt-1">Add your first wholesaler to start sharing deals</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {wholesalers.map(w => (
            <Card key={w.id} data-testid={`wholesaler-row-${w.id}`} className="bg-white border-gray-200 hover:border-[#0073ea]/30 hover:shadow-md transition-all duration-200 group">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-gray-500">{(w.name || '?').charAt(0).toUpperCase()}</span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p data-testid={`wholesaler-name-${w.id}`} className="text-sm font-semibold text-gray-900">{w.name}</p>
                      {w.company && <span className="text-xs text-gray-500">at {w.company}</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {w.phone && (
                        <span data-testid={`wholesaler-phone-${w.id}`} className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-gray-400" />
                          {w.phone}
                        </span>
                      )}
                      {w.email && (
                        <span data-testid={`wholesaler-email-${w.id}`} className="flex items-center gap-1">
                          <Mail className="h-3 w-3 text-gray-400" />
                          {w.email}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Deals Sold */}
                  <div data-testid={`wholesaler-deals-${w.id}`} className="text-right min-w-[80px] shrink-0">
                    <p className="text-sm font-bold text-gray-900">{w.deals_sold || 0}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Deals Sold</p>
                  </div>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-900 opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white border-gray-200 shadow-lg">
                      <DropdownMenuItem data-testid={`edit-wholesaler-${w.id}`} onClick={() => handleEdit(w)} className="text-gray-700 hover:bg-gray-50">
                        <Pencil className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`delete-wholesaler-${w.id}`} onClick={() => handleDelete(w.id)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditId(null); setForm(defaultForm); } }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900">{editId ? 'Edit Wholesaler' : 'Add Wholesaler'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-gray-700">Name *</Label>
              <Input data-testid="wholesaler-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Full name" className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Company</Label>
              <Input data-testid="wholesaler-company-input" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Company name" className={inputClass} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Phone</Label>
                <Input data-testid="wholesaler-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Email</Label>
                <Input data-testid="wholesaler-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className={inputClass} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Deals Sold</Label>
              <Input data-testid="wholesaler-deals-input" type="number" value={form.deals_sold} onChange={e => setForm({ ...form, deals_sold: e.target.value })} placeholder="0" className={inputClass} />
            </div>
            <Button type="submit" data-testid="wholesaler-submit-btn" disabled={creating} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">
              {creating ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (editId ? 'Update' : 'Add Wholesaler')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
